import { useEffect, useState } from 'preact/hooks';
import { XP_REASON_LABELS, isPvpMode, type PlayerOutcome } from '@cratered/shared';
import { sfx } from '../game/audio';
import { TEAM_COLORS } from '../game/render';
import { isHost, lobby, match, net, results, screen, self } from '../store';
import { Avatar, MoleCanvas, TeamDot } from './common';

const BONUS = new Set(['flawless', 'domination', 'quick', 'comeback', 'dailyFirstWin', 'stars']);

export function Results() {
  const r = results.value;
  const m = match.value;
  const myId = self.value?.id ?? '';
  const [fill, setFill] = useState(0);
  const mine = r?.rewards[myId];

  // The result sting, once, when the screen comes up.
  useEffect(() => {
    const o = r?.outcomes.find((x) => x.playerId === myId);
    if (o) sfx.fanfare(o.result === 'win' ? 'win' : o.result === 'loss' ? 'lose' : 'draw');
  }, [r]);

  useEffect(() => {
    if (!mine) return;
    setFill(mine.levelBefore.progress);
    const t = setTimeout(() => setFill(mine.levelAfter.progress), 300);
    return () => clearTimeout(t);
  }, [mine?.xpGained]);

  if (!r || !m) return null;
  const ranked = [...r.outcomes].sort(rank);
  const nameFor = (o: PlayerOutcome) => m.players.find((p) => p.id === o.playerId);
  const host = isHost.value;
  const hostName = lobby.value?.players.find((p) => p.id === lobby.value?.hostId)?.name ?? 'the host';
  // Versus and brawl are team games, so they get ranked standings with team dots; PvE is a shared win or loss.
  const pvp = isPvpMode(m.settings.mode);

  return (
    <div class="screen">
      <div class="results">
        <section class="panel">
          <div class="panel-head">
            <h2>{pvp ? 'Final standings' : ranked[0]?.result === 'win' ? 'Victory' : 'Defeat'}</h2>
            <span class="muted num">{ranked[0]?.roundsPlayed ?? 0} rounds</span>
          </div>
          <div class="panel-body">
            {ranked.map((o, i) => {
              const p = nameFor(o);
              const rw = r.rewards[o.playerId];
              return (
                <div key={o.playerId} class={`rank-row${i === 0 && o.result === 'win' ? ' first' : ''}`}>
                  <span class="rank num">{pvp ? i + 1 : o.result === 'win' ? '★' : '—'}</span>
                  <Avatar name={p?.name ?? '?'} url={p?.avatarUrl} large />
                  <div style={{ minWidth: 0 }}>
                    <div class="name" style={{ fontWeight: 800, display: 'flex', gap: 8, alignItems: 'center' }}>
                      {pvp && <TeamDot team={p?.team ?? 0} />}
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p?.name ?? o.playerId}</span>
                      {o.playerId === myId && <span class="muted">(you)</span>}
                    </div>
                    <div class="muted num" style={{ fontSize: 12 }}>
                      {o.result === 'win' ? 'Won' : o.result === 'draw' ? 'Draw' : 'Lost'} · {o.unitsAlive}/{o.unitsStart} moles · {o.hpRemaining} HP left
                      {rw && rw.levelAfter.level > rw.levelBefore.level ? ` · Level up → ${rw.levelAfter.level}` : ''}
                    </div>
                  </div>
                  <span class="num" style={{ fontWeight: 800, color: 'var(--accent)' }}>
                    {rw ? `+${rw.xpGained} XP` : ''}
                  </span>
                </div>
              );
            })}
            <div class="btn-row" style={{ justifyContent: 'flex-end', marginTop: 8 }}>
              <button class="btn ghost" onClick={() => (screen.value = 'locker')}>
                Locker
              </button>
              {host ? (
                <>
                  <button class="btn" onClick={() => net.value?.send({ t: 'backToLobby' })}>
                    Back to lobby
                  </button>
                  <button
                    class="btn primary big"
                    onClick={() => {
                      net.value?.send({ t: 'backToLobby' });
                      setTimeout(() => net.value?.send({ t: 'startMatch' }), 150);
                    }}
                  >
                    Play again
                  </button>
                </>
              ) : (
                <span class="muted">Waiting for {hostName}…</span>
              )}
            </div>
          </div>
        </section>

        <section class="panel">
          <div class="panel-head">
            <h2>Your XP</h2>
            {mine && <span class="muted num">Lv {mine.levelBefore.level}{mine.levelAfter.level !== mine.levelBefore.level ? ` → ${mine.levelAfter.level}` : ''}</span>}
          </div>
          <div class="panel-body">
            {mine ? (
              <>
                <div class="xp-bar" aria-hidden="true">
                  <span style={{ width: `${fill * 100}%` }} />
                </div>
                <div>
                  {mine.breakdown.lines.map((l) => (
                    <div key={l.reason} class={`xp-line${BONUS.has(l.reason) ? ' bonus' : ''}`}>
                      <span>{XP_REASON_LABELS[l.reason]}</span>
                      <span class="count num">{l.count > 1 || l.reason === 'hit' || l.reason === 'kill' ? `×${l.count}` : ''}</span>
                      <span class="amount num">+{l.amount}</span>
                    </div>
                  ))}
                  <div class="xp-total">
                    <span>Total</span>
                    <span class="big num">+{mine.xpGained}</span>
                  </div>
                </div>
                {mine.newUnlocks.length > 0 && (
                  <>
                    <p class="eyebrow">New unlocks</p>
                    {mine.newUnlocks.map((u) => (
                      <div key={u.id} class="new-unlock">
                        <div class="card-art">
                          {u.kind === 'body' || u.kind === 'outfit' ? (
                            <MoleCanvas look={{ body: u.kind === 'body' ? u.id : 'body.lean', outfit: u.kind === 'outfit' ? u.id : 'outfit.none', teamColor: TEAM_COLORS[0] }} />
                          ) : (
                            <span style={{ fontFamily: 'var(--font-display)', fontSize: 22, color: 'var(--warn)' }}>★</span>
                          )}
                        </div>
                        <div>
                          <div class="card-name">{u.name}</div>
                          <div class="card-desc">{u.kind === 'perk' ? 'Perk · Co-op & Campaign' : u.description}</div>
                        </div>
                      </div>
                    ))}
                  </>
                )}
              </>
            ) : (
              <p class="muted">You were spectating. Join the next one!</p>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}

function rank(a: PlayerOutcome, b: PlayerOutcome): number {
  const score = (o: PlayerOutcome) => (o.result === 'win' ? 2 : o.result === 'draw' ? 1 : 0);
  return score(b) - score(a) || b.hpRemaining - a.hpRemaining || b.unitsAlive - a.unitsAlive;
}
